import { useState, useEffect } from "react";
import { API_BASE } from "../App";

const STRATEGIES = [
  { key: "short",        label: "Short",        color: "#E85D4A", days: "1-6 weeks"   },
  { key: "short_medium", label: "Short-Medium",  color: "#F5A623", days: "2-6 months"  },
  { key: "medium_long",  label: "Medium-Long",   color: "#4A90E2", days: "6-12 months" },
  { key: "long",         label: "Long",          color: "#9B59B6", days: "1-2 years"   },
];

const LAYERS = [
  "technical","tcn","xgboost","sentiment","graph",
  "stat_arb","options","insider","short_interest","cross_asset","momentum"
];

function useAPI(endpoint, token) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!endpoint || !token) return;
    setLoading(true);
    fetch(`${API_BASE}${endpoint}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [endpoint, token]);
  return { data, loading };
}

function MiniChart({ trades, rollingAvg, rollingBest, rollingLower, rollingUpper }) {
  if (!trades || trades.length === 0) return (
    <div style={{ height: 220, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--color-text-secondary)", fontSize: 13 }}>
      No closed trades yet — data will populate as paper trades close
    </div>
  );
  const n = trades.length;
  const W = 600, H = 200, PAD = 40;
  const allVals = [...rollingAvg, ...rollingBest, ...rollingLower, ...rollingUpper,
                   ...trades.map(t => t.pct_change), ...trades.map(t => t.best_case_pct)];
  const minV = Math.min(...allVals) - 2;
  const maxV = Math.max(...allVals) + 2;
  const range = maxV - minV || 1;
  const xScale = i => PAD + (i / Math.max(n - 1, 1)) * (W - PAD * 2);
  const yScale = v => PAD + (1 - (v - minV) / range) * (H - PAD * 2);
  const toPath = arr => arr.map((v, i) => `${i === 0 ? "M" : "L"} ${xScale(i)} ${yScale(v)}`).join(" ");
  const orangeThreshold = 30;
  const orangeBest = rollingBest.map((v, i) => {
    const avg = rollingAvg[i] ?? v;
    const diff = v - avg;
    return diff > orangeThreshold ? avg + orangeThreshold : v;
  });
  const toArea = (top, bot) => {
    const tp = top.map((v, i) => `${i === 0 ? "M" : "L"} ${xScale(i)} ${yScale(v)}`).join(" ");
    const bp = [...bot].reverse().map((v, i) => `L ${xScale(bot.length-1-i)} ${yScale(v)}`).join(" ");
    return `${tp} ${bp} Z`;
  };
  const zero = yScale(0);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: 220 }}>
      <line x1={PAD} y1={zero} x2={W-PAD} y2={zero} stroke="var(--color-border-tertiary)" strokeWidth="1" strokeDasharray="4,3" />
      <path d={toArea(rollingBest, orangeBest)} fill="rgba(232,93,74,0.18)" />
      <path d={toArea(orangeBest, rollingAvg)} fill="rgba(245,166,35,0.18)" />
      <path d={toArea(rollingUpper, rollingLower)} fill="rgba(74,155,111,0.10)" />
      {trades.map((t, i) => (
        <circle key={i} cx={xScale(i)} cy={yScale(t.pct_change)} r="3"
          fill={t.outcome==="WIN"?"#4A9B6F":t.outcome==="LOSS"?"#E85D4A":"#888"} opacity="0.65" />
      ))}
      {trades.map((t, i) => (
        <circle key={`bc-${i}`} cx={xScale(i)} cy={yScale(t.best_case_pct)} r="2" fill="#9B59B6" opacity="0.5" />
      ))}
      <path d={toPath(rollingAvg)}   fill="none" stroke="#4A9B6F" strokeWidth="2.5" />
      <path d={toPath(orangeBest)}   fill="none" stroke="#F5A623" strokeWidth="1.5" strokeDasharray="5,3" />
      <path d={toPath(rollingBest)}  fill="none" stroke="#E85D4A" strokeWidth="1.5" strokeDasharray="3,3" />
      <path d={toPath(rollingLower)} fill="none" stroke="#4A9B6F" strokeWidth="1"   opacity="0.3" />
      <path d={toPath(rollingUpper)} fill="none" stroke="#4A9B6F" strokeWidth="1"   opacity="0.3" />
      <text x={PAD}   y={H-6} fill="var(--color-text-secondary)" fontSize="10">Trade 1</text>
      <text x={W-PAD} y={H-6} fill="var(--color-text-secondary)" fontSize="10" textAnchor="end">Trade {n}</text>
      <text x={6} y={yScale(maxV)+4} fill="var(--color-text-secondary)" fontSize="10" textAnchor="middle">{maxV.toFixed(0)}%</text>
      <text x={6} y={yScale(minV)+4} fill="var(--color-text-secondary)" fontSize="10" textAnchor="middle">{minV.toFixed(0)}%</text>
    </svg>
  );
}

function LayerBars({ layers }) {
  if (!layers) return null;
  const maxCorr = Math.max(...Object.values(layers).map(l => Math.abs(l.correlation_with_return||0)), 0.01);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {LAYERS.filter(l => layers[l]).map(layer => {
        const d = layers[layer];
        const corr = d.correlation_with_return || 0;
        const barW = Math.abs(corr) / maxCorr * 100;
        return (
          <div key={layer} style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ width: 100, fontSize: 12, color: "var(--color-text-secondary)", textTransform: "capitalize" }}>
              {layer.replace(/_/g, " ")}
            </span>
            <div style={{ flex: 1, height: 8, background: "var(--color-background-secondary)", borderRadius: 4, overflow: "hidden" }}>
              <div style={{ width: `${barW}%`, height: "100%", borderRadius: 4, background: corr >= 0 ? "#4A9B6F" : "#E85D4A" }} />
            </div>
            <span style={{ width: 44, fontSize: 12, textAlign: "right", color: corr >= 0 ? "#4A9B6F" : "#E85D4A" }}>
              {corr.toFixed(2)}
            </span>
            <span style={{ width: 32, fontSize: 11, color: "var(--color-text-secondary)", textAlign: "right" }}>
              {d.trade_count}t
            </span>
          </div>
        );
      })}
    </div>
  );
}

function TradeTable({ trades }) {
  if (!trades || trades.length === 0) return (
    <p style={{ color: "var(--color-text-secondary)", fontSize: 13, textAlign: "center", padding: "32px 0" }}>
      No closed trades yet
    </p>
  );
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr>
            {["Ticker","Return","Best Case","Left on Table","Outcome","Days Held","Exit Reason"].map(h => (
              <th key={h} style={{ padding: "8px 12px", textAlign: "left", color: "var(--color-text-secondary)", fontWeight: 500, borderBottom: "0.5px solid var(--color-border-tertiary)", fontSize: 11 }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {[...trades].reverse().map((t, i) => {
            const leftOn = t.best_case_pct - t.pct_change;
            const leftColor = leftOn > 30 ? "#E85D4A" : leftOn > 10 ? "#F5A623" : "var(--color-text-secondary)";
            return (
              <tr key={i} style={{ borderBottom: "0.5px solid var(--color-border-tertiary)" }}>
                <td style={{ padding: "8px 12px", fontWeight: 500 }}>{t.ticker}</td>
                <td style={{ padding: "8px 12px", color: t.pct_change >= 0 ? "#4A9B6F" : "#E85D4A" }}>
                  {t.pct_change >= 0 ? "+" : ""}{t.pct_change?.toFixed(2)}%
                </td>
                <td style={{ padding: "8px 12px", color: "#9B59B6" }}>
                  +{t.best_case_pct?.toFixed(2)}%
                </td>
                <td style={{ padding: "8px 12px", color: leftColor, fontSize: 11 }}>
                  {leftOn > 0 ? `+${leftOn.toFixed(2)}%` : "—"}
                </td>
                <td style={{ padding: "8px 12px" }}>
                  <span style={{
                    padding: "2px 8px", borderRadius: 4, fontSize: 10,
                    background: t.outcome==="WIN"?"rgba(74,155,111,0.15)":t.outcome==="LOSS"?"rgba(232,93,74,0.15)":"rgba(136,136,136,0.15)",
                    color: t.outcome==="WIN"?"#4A9B6F":t.outcome==="LOSS"?"#E85D4A":"#888",
                  }}>{t.outcome||"—"}</span>
                </td>
                <td style={{ padding: "8px 12px", color: "var(--color-text-secondary)" }}>{t.days_held||"—"}d</td>
                <td style={{ padding: "8px 12px", color: "var(--color-text-secondary)", fontSize: 11 }}>
                  {t.barrier_hit?.replace(/_/g," ")||"—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function TradePerformance({ token }) {
  const [activeStrategy, setActiveStrategy] = useState("short");
  const [activeTab, setActiveTab] = useState("chart");
  const { data: mcData, loading: mcLoading } = useAPI(`/api/trades/monte-carlo/${activeStrategy}`, token);
  const { data: layerData } = useAPI(`/api/trades/layers/${activeStrategy}`, token);
  const { data: summaryData } = useAPI("/api/trades/summary", token);
  const strategy = STRATEGIES.find(s => s.key === activeStrategy);
  const getTradeCount = (key) => summaryData?.[key]?.total_trades || 0;

  return (
    <div style={{ padding: "32px 0" }}>
      <div style={{ marginBottom: 28 }}>
        <h2 style={{ fontSize: 22, fontWeight: 500, margin: "0 0 4px" }}>Trade Performance</h2>
        <p style={{ color: "var(--color-text-secondary)", fontSize: 13, margin: 0 }}>
          Recency-weighted rolling analysis — recent trades carry more weight as the model improves
        </p>
      </div>

      <div style={{ display: "flex", gap: 2, marginBottom: 28, borderBottom: "1px solid #333" }}>
        {STRATEGIES.map(s => {
          const count = getTradeCount(s.key);
          const active = activeStrategy === s.key;
          return (
            <button key={s.key} onClick={() => { setActiveStrategy(s.key); setActiveTab("chart"); }} style={{
              background: "transparent",
              border: "none",
              borderBottom: active ? "2px solid #7D1525" : "2px solid transparent",
              padding: "12px 20px",
              cursor: "pointer",
              transition: "all 0.15s",
              display: "flex", alignItems: "center", gap: 8,
            }}>
              <span style={{
                fontWeight: 500,
                fontSize: 11,
                letterSpacing: 2,
                textTransform: "uppercase",
                color: active ? "#7D1525" : "#888",
              }}>{s.label}</span>
              <span style={{ fontSize: 10, color: "#666" }}>{s.days}</span>
              <span style={{
                background: active ? "rgba(125,21,37,0.15)" : "rgba(255,255,255,0.05)",
                color: active ? "#7D1525" : "#666",
                borderRadius: 10, padding: "1px 8px", fontSize: 10, fontWeight: 500,
              }}>{count}</span>
            </button>
          );
        })}
      </div>

      {mcData && mcData.trade_count > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))", gap: 12, marginBottom: 24 }}>
          {[
            { label: "Total Trades",      value: mcData.trade_count, color: null },
            { label: "Avg Return",        value: `${mcData.avg_return >= 0 ? "+" : ""}${mcData.avg_return?.toFixed(2)}%`, color: mcData.avg_return >= 0 ? "#4A9B6F" : "#E85D4A" },
            { label: "Avg Best Case",     value: `+${mcData.avg_best_case?.toFixed(2)}%`, color: "#9B59B6" },
            { label: "Avg Left on Table", value: `+${mcData.avg_left_on_table?.toFixed(2)}%`, color: mcData.avg_left_on_table > 30 ? "#E85D4A" : "#F5A623" },
          ].map(card => (
            <div key={card.label} style={{ background: "var(--color-background-secondary)", borderRadius: "var(--border-radius-md)", padding: "14px 16px" }}>
              <p style={{ fontSize: 11, color: "var(--color-text-secondary)", margin: "0 0 6px" }}>{card.label}</p>
              <p style={{ fontSize: 20, fontWeight: 500, margin: 0, color: card.color || "var(--color-text-primary)" }}>{card.value}</p>
            </div>
          ))}
        </div>
      )}

      <div style={{ background: "var(--color-background-primary)", border: "0.5px solid var(--color-border-tertiary)", borderRadius: "var(--border-radius-lg)", overflow: "hidden" }}>
        <div style={{ display: "flex", borderBottom: "0.5px solid var(--color-border-tertiary)", padding: "0 16px" }}>
          {["chart","trades","layers"].map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)} style={{
              background: "transparent", border: "none",
              borderBottom: activeTab === tab ? "2px solid #8B6914" : "2px solid transparent",
              padding: "12px 16px", fontSize: 12, cursor: "pointer",
              color: activeTab === tab ? strategy?.color : "var(--color-text-secondary)",
              textTransform: "capitalize", fontWeight: activeTab === tab ? 500 : 400,
            }}>{tab === "chart" ? "Monte Carlo" : tab === "layers" ? "Signal Layers" : "Trade Log"}</button>
          ))}
        </div>

        <div style={{ padding: 20 }}>
          {mcLoading && <p style={{ color: "var(--color-text-secondary)", textAlign: "center", padding: 40, fontSize: 13 }}>Loading...</p>}

          {!mcLoading && activeTab === "chart" && (
            <div>
              <div style={{ display: "flex", gap: 16, marginBottom: 14, flexWrap: "wrap" }}>
                {[
                  { color: "#4A9B6F", label: "Rolling avg return",  dash: false },
                  { color: "#F5A623", label: "Moderate gap < 30%",  dash: true  },
                  { color: "#E85D4A", label: "Major gap > 30%",     dash: true  },
                  { color: "#4A9B6F", label: "Win",                 dot: true   },
                  { color: "#E85D4A", label: "Loss",                dot: true   },
                  { color: "#9B59B6", label: "Best case",           dot: true   },
                ].map((item, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "var(--color-text-secondary)" }}>
                    {item.dot
                      ? <div style={{ width: 8, height: 8, borderRadius: "50%", background: item.color }} />
                      : <div style={{ width: 20, height: 0, borderTop: `2px ${item.dash ? "dashed" : "solid"} ${item.color}` }} />
                    }
                    {item.label}
                  </div>
                ))}
              </div>
              <MiniChart
                trades={mcData?.trades||[]}
                rollingAvg={mcData?.rolling_avg||[]}
                rollingBest={mcData?.rolling_best||[]}
                rollingLower={mcData?.rolling_lower||[]}
                rollingUpper={mcData?.rolling_upper||[]}
              />
              <p style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 8, textAlign: "center" }}>
                Green line = rolling avg return. Orange fill = moderate left-on-table. Red fill = major gap vs best case.
                Confidence band narrows as trade count grows.
              </p>
            </div>
          )}

          {!mcLoading && activeTab === "trades" && <TradeTable trades={mcData?.trades||[]} />}

          {!mcLoading && activeTab === "layers" && (
            <div>
              <p style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 16 }}>
                Correlation between each signal layer score and actual trade return.
                Green = correctly predicted direction. Red = noise or inverse.
              </p>
              <LayerBars layers={layerData} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
